import { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { useNotificationHelpers } from './useNotificationHelpers';
import { addPushToken, getTrackingId } from '@nusrif/services';
import { useAuth } from './useAuth';

interface UsePushNotificationReturn {
  showPermissionModal: boolean;
  setShowPermissionModal: (show: boolean) => void;
  requestPermission: () => Promise<void>;
  hasPermission: boolean;
  isLoading: boolean;
  sendTokenToBackend: (token: string, platform: 'android' | 'ios' | 'web') => Promise<void>;
}

export const usePushNotification = (): UsePushNotificationReturn => {
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { showSuccess, showError } = useNotificationHelpers();
  const { user } = useAuth();

  // Check current permission status (without requesting)
  const checkPermission = useCallback(async () => {
    if (Capacitor.getPlatform() === 'web') {
      // Check permission status without requesting
      const permission = Notification.permission;
      setHasPermission(permission === 'granted');
      return permission === 'granted';
    } else {
      // For native platforms, check FCM permission
      try {
        const permission = await FirebaseMessaging.checkPermissions();
        setHasPermission(permission.receive === 'granted');
        return permission.receive === 'granted';
      } catch (error) {
        console.error('Error checking FCM permissions:', error);
        return false;
      }
    }
  }, []);

  // Function to send token to backend
  const sendTokenToBackend = useCallback(async (token: string, platform: 'android' | 'ios' | 'web') => {
    try {
      const payload = {
        token,
        platform,
        deviceType: platform === 'web' ? 'browser' : 'mobile',
      };

      if (user?.id) {
        // User is authenticated - use userId
        await addPushToken({
          ...payload,
          userId: user.id,
        });
        console.log(`✅ Push token registered for authenticated user on ${platform}`);
      } else {
        // User is not authenticated - use tracking ID
        const distinctId = getTrackingId();
        await addPushToken({
          ...payload,
          distinctId,
        });
        console.log(`✅ Push token registered for anonymous user (${distinctId}) on ${platform}`);
      }
    } catch (error) {
      console.error(`❌ Failed to register push token for ${platform}:`, error);
    }
  }, [user?.id]);

  // Request permission
  const requestPermission = useCallback(async () => {
    setIsLoading(true);
    try {
      if (Capacitor.getPlatform() === 'web') {
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          setHasPermission(true);
          // Get FCM token for web after permission is granted
          try {
            const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
            const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
            
            // Note: VAPID key should be configured in your app config
            const { token } = await FirebaseMessaging.getToken({
              serviceWorkerRegistration: registration,
            });
            console.log("🔑 Web FCM token after permission:", token);
            
            // Send token to backend
            if (token) {
              await sendTokenToBackend(token, 'web');
            }
          } catch (tokenError) {
            console.error('Error getting FCM token:', tokenError);
          }
          showSuccess('Notifications Enabled!', 'You\'ll now receive updates about your orders and offers.');
        } else {
          showError('Permission Denied', 'You can enable notifications later in your browser settings.');
        }
      } else {
        // For native platforms
        const permission = await FirebaseMessaging.requestPermissions();
        if (permission.receive === 'granted') {
          setHasPermission(true);
          // Get FCM token for native after permission is granted
          try {
            const { token } = await FirebaseMessaging.getToken();
            console.log("🔑 Native FCM token after permission:", token);
            
            // Send token to backend
            if (token) {
              const platform = Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';
              await sendTokenToBackend(token, platform);
            }
          } catch (tokenError) {
            console.error('Error getting FCM token:', tokenError);
          }
          showSuccess('Notifications Enabled!', 'You\'ll now receive updates about your orders and offers.');
        } else {
          showError('Permission Denied', 'You can enable notifications later in your device settings.');
        }
      }
    } catch (error) {
      console.error('Error requesting permission:', error);
      showError('Permission Error', 'Failed to request notification permission. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [showSuccess, showError, sendTokenToBackend]);

  // Check permission on mount
  useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  // Show modal on first app launch if permission not granted
  useEffect(() => {
    const shouldShowModal = () => {
      if (hasPermission) return false;
      
      // Check if this is the first time the user has launched the app
      const hasLaunchedBefore = localStorage.getItem('appFirstLaunch');
      if (hasLaunchedBefore) {
        // Not first launch - check if we've shown the modal recently (within 7 days)
        const lastShown = localStorage.getItem('pushNotificationModalLastShown');
        if (lastShown) {
          const lastShownDate = new Date(lastShown);
          const sevenDaysAgo = new Date();
          sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
          
          if (lastShownDate > sevenDaysAgo) {
            return false;
          }
        }
      } else {
        // First launch - mark it and show modal
        localStorage.setItem('appFirstLaunch', 'true');
      }
      
      return true;
    };

    // Show modal immediately on first launch, or after delay for subsequent launches
    const hasLaunchedBefore = localStorage.getItem('appFirstLaunch');
    const delay = hasLaunchedBefore ? 3000 : 0; // 0 delay for first launch, 3s for others

    const timer = setTimeout(() => {
      if (shouldShowModal()) {
        setShowPermissionModal(true);
        localStorage.setItem('pushNotificationModalLastShown', new Date().toISOString());
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [hasPermission]);

  return {
    showPermissionModal,
    setShowPermissionModal,
    requestPermission,
    hasPermission,
    isLoading,
    sendTokenToBackend,
  };
}; 